import React from 'react';
import { clsx } from 'clsx';
import { shopVar } from '@/lib/shopStyles';

interface MainButtonProps {
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  /**
   * Called when the visitor reaches for the button while it is disabled — hovering it, or tapping
   * it on a phone, where there is no hover. A disabled button normally swallows both, so this is
   * the only way anything upstream can hear "I tried to continue and nothing happened".
   */
  onDisabledInteraction?: () => void;
  children: React.ReactNode;
  className?: string;
  url?: string;
  newTab?: boolean;
  style?: React.CSSProperties;
}

export const MainButton: React.FC<MainButtonProps> = ({ disabled, loading, onClick, onDisabledInteraction, children, className, url, newTab, style }) => {
  // Keeping a dead button reachable is the whole point of onDisabledInteraction, so it is the one
  // case where the button does not opt out of pointer events. Still not clickable: the click goes
  // to onDisabledInteraction, never to onClick. A loading button is busy, not stuck — leave it be.
  const reachableWhileDisabled = Boolean(onDisabledInteraction) && Boolean(disabled) && !loading && !url;
  const commonProps = {
    className: clsx(
      'flex items-center justify-center rounded-md px-4 py-2 group relative',
      {
        'hover:cursor-pointer hover:opacity-80': !disabled && !loading,
        'opacity-40': disabled || loading,
        'pointer-events-none': (disabled || loading) && !reachableWhileDisabled,
        'cursor-not-allowed': reachableWhileDisabled,
      },
      className
    ),
    onMouseEnter: reachableWhileDisabled ? onDisabledInteraction : undefined,
    style: {
      backgroundColor: shopVar('--shop-ctaButtonColor'),
      color: shopVar('--shop-ctaButtonTextColor'),
      ...style
    },
    role: "button",
    tabIndex: disabled || loading ? -1 : 0,
  };

  const content = (
    <>
      <div className={clsx('flex items-center justify-center w-full transition-opacity', { 'opacity-0': loading })}>
        {children}
      </div>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-t-transparent" style={{
            borderColor: `${shopVar('--shop-ctaButtonTextColor')} transparent ${shopVar('--shop-ctaButtonTextColor')} ${shopVar('--shop-ctaButtonTextColor')}`
          }}/>
        </div>
      )}
    </>
  );

  if (url) {
    return (
      <a href={url} {...commonProps} target={newTab ? '_blank' : '_self'}>
        {content}
      </a>
    );
  }
  return (
    <div
      {...commonProps}
      onClick={!disabled && !loading ? onClick : reachableWhileDisabled ? onDisabledInteraction : undefined}
    >
      {content}
    </div>
  );
};

export default MainButton;